import { useState, useEffect, useRef } from 'react';
import {
  getAllOfficerRoles,
  addOfficerRole,
  updateOfficerRole,
  deleteOfficerRole,
  updateSortOrder,
} from '../../../../services/officerRoles';
import { getOfficerRoleCount } from '../../../../services/users';
import PrismDataGrid, {
  type DataGridColumn,
} from '../../../shared/PrismDataGrid';
import {
  PrismBanner,
  PrismButton,
  PrismCheckbox,
  PrismDialog,
  PrismInputText,
  PrismLoading,
  PrismSelect,
  PrismSwitch,
} from '@prism-ui/react';
import { toast } from '../../../Toaster/toast';
import './OfficerRoles.css';
import {
  updateAdminSetting,
  getAdminSetting,
} from '../../../../services/AdminSettings';
import axios from 'axios';
import AdminPane from '../../AdminPane';

type OfficerRole = { id: number; name: string; sortOrder: number };
type User = { id: number; name: string; email: string };

const OfficerRoles = () => {
  const [roles, setRoles] = useState<OfficerRole[]>([]);
  const [newRole, setNewRole] = useState('');
  const [loading, setLoading] = useState(true);
  
  // Modify modal state
  const [showModifyModal, setShowModifyModal] = useState(false);
  const [roleToModify, setRoleToModify] = useState<OfficerRole | null>(null);
  const [roleToModifyUserCount, setRoleToModifyUserCount] = useState<number>(0);
  
  // Delete confirmation modal state
  const [showFinalDeleteConfirmModal, setShowFinalDeleteConfirmModal] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [finalDeleteModalError, setFinalDeleteModalError] = useState('');
  const [showFinalDeleteAffectedUsers, setShowFinalDeleteAffectedUsers] = useState(false);
  const [finalDeleteAffectedUsers, setFinalDeleteAffectedUsers] = useState<User[]>([]);
  
  // Migrate modal state
  const [showMigrateModal, setShowMigrateModal] = useState(false);
  const [selectedMigrateToRole, setSelectedMigrateToRole] = useState<number | null>(null);
  const [createNewMigrateRole, setCreateNewMigrateRole] = useState(false);
  const [newMigrateRoleName, setNewMigrateRoleName] = useState('');
  const [showMigrateReviewModal, setShowMigrateReviewModal] = useState(false);
  const [showAffectedUsers, setShowAffectedUsers] = useState(false);
  const [affectedUsers, setAffectedUsers] = useState<User[]>([]);
  
  // Add users modal state
  const [showAddUsersModal, setShowAddUsersModal] = useState(false);
  const [roleToAddUsers, setRoleToAddUsers] = useState<OfficerRole | null>(null);
  const [availableUsers, setAvailableUsers] = useState<User[]>([]);
  const [selectedUsersToAdd, setSelectedUsersToAdd] = useState<number[]>([]);
  const [loadingAvailableUsers, setLoadingAvailableUsers] = useState(false);
  const [userSearchFilter, setUserSearchFilter] = useState('');
  const [showAddUsersReviewModal, setShowAddUsersReviewModal] = useState(false);
  const [showAddUsersAffectedUsers, setShowAddUsersAffectedUsers] = useState(false);
  
  const [sortOrder, setSortOrder] = useState<'alphabetical' | 'random'>(
    'alphabetical'
  );
  const newRoleInputRef = useRef<HTMLPrismInputTextElement>(null);

  useEffect(() => {
    const fetchRoles = async () => {
      try {
        setLoading(true);
        const fetchedRoles = await getAllOfficerRoles();
        setRoles(fetchedRoles);

        // Fetch admin setting
        getAdminSetting('officer_roles_sort_order')
          .then((setting: { setting_value: string }) => {
            if (
              setting.setting_value === 'alphabetical' ||
              setting.setting_value === 'random'
            ) {
              setSortOrder(setting.setting_value);
            }
          })
          .catch((error: unknown) => {
            console.error('Error fetching admin setting:', error);
            // Fallback to default
            setSortOrder('alphabetical');
          });
      } catch (error) {
        console.error('Error fetching roles:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchRoles();
  }, []);

  const toggleSortOrder = async () => {
    try {
      const newOrder = sortOrder === 'alphabetical' ? 'random' : 'alphabetical';
      await updateAdminSetting('officer_roles_sort_order', newOrder);
      setSortOrder(newOrder);
      toast.success(`Sort order changed to ${newOrder}`);
    } catch (error) {
      console.error('Error updating sort order:', error);
      toast.error('Failed to update sort order');
    }
  };

  const sortedRoles = [...roles].sort((a, b) => {
    return a.sortOrder - b.sortOrder;
  });

  const handleAdd = async () => {
    // Check if role name is blank
    if (!newRole.trim()) {
      toast.error('Role name cannot be blank');
      return;
    }
    
    // Check if role name already exists
    if (roles.some((role) => role.name === newRole.trim())) {
      toast.error('Role name already exists');
      return;
    }
    
    try {
      setLoading(true);

      // Get the next sort order
      const maxSortOrder =
        roles.length > 0
          ? Math.max(...roles.map((role) => role.sortOrder || 0))
          : 0;
      const newSortOrder = maxSortOrder + 1;

      const newRoleObj = {
        name: newRole.trim(),
        sortOrder: newSortOrder,
      };

      // Call API to create the role
      const createdRole = await addOfficerRole(newRoleObj);

      // Validate response structure
      if (!createdRole || typeof createdRole.id !== 'number') {
        throw new Error('Invalid response from server');
      }

      // Add to local state
      setRoles((prevRoles) =>
        [...prevRoles, createdRole].sort((a, b) => a.sortOrder - b.sortOrder)
      );
      setNewRole('');
      toast.success(`Successfully added role "${createdRole.name}"`);
      // Refocus the input for adding another role
      setTimeout(() => newRoleInputRef.current?.setFocus(), 0);
    } catch (error) {
      console.error('Error adding role:', error);
      // Don't crash the component - show an error message instead
      const errMsg =
        error instanceof Error ? error.message : String(error);
      toast.error('Failed to add role: ' + errMsg);
    } finally {
      setLoading(false);
    }
  };

  const saveRoleName = async (row: OfficerRole, newValue: string) => {
    if (newValue === row.name) return;
    try {
      await updateOfficerRole({
        id: row.id,
        name: newValue,
        sortOrder: row.sortOrder,
      });
      setRoles(
        roles.map((role) =>
          role.id === row.id ? { ...role, name: newValue } : role
        )
      );
      toast.success(`Successfully updated role name to "${newValue}"`);
    } catch (error) {
      console.error('Error updating role:', error);
      toast.error('Failed to update role. Please try again.');
    }
  };

  const saveSortOrder = async (row: OfficerRole, newValue: string) => {
    const newSortOrder = parseInt(newValue, 10);
    if (newSortOrder === row.sortOrder) return;
    try {
      // Get roles sorted by current sort order
      const sortedRolesByOrder = [...roles].sort((a, b) => a.sortOrder - b.sortOrder);

      // Find the role being edited
      const roleIndex = sortedRolesByOrder.findIndex((r) => r.id === row.id);
      const role = sortedRolesByOrder[roleIndex];

      // Remove the role from its current position
      sortedRolesByOrder.splice(roleIndex, 1);

      // Insert it at the new position (convert to 0-based index)
      const newIndex = Math.max(
        0,
        Math.min(newSortOrder - 1, sortedRolesByOrder.length)
      );
      sortedRolesByOrder.splice(newIndex, 0, role);

      // Resequence all roles to be 1, 2, 3, 4...
      const resequencedRoles = sortedRolesByOrder.map((r, index) => ({
        ...r,
        sortOrder: index + 1,
      }));

      // Prepare payload for backend - send all roles with their new sort orders
      const payload = {
        roles: resequencedRoles.map((r) => ({
          id: r.id,
          sort_order: r.sortOrder,
        })),
      };

      await updateSortOrder(payload);

      setRoles(resequencedRoles);
      toast.success(
        `Successfully reordered "${row.name}" to position ${newSortOrder}`
      );
    } catch (error) {
      console.error('Error updating role:', error);
      toast.error('Failed to update role. Please try again.');
    }
  };

  const handleModifyClick = async (roleId: number) => {
    const role = roles.find((r) => r.id === roleId);

    if (role) {
      setRoleToModify(role);
      setRoleToModifyUserCount(0); // Reset before fetching

      const data = await getOfficerRoleCount(role.id);
      setRoleToModifyUserCount(data.userCount);

      setShowModifyModal(true);
    }
  };

  const handleCancelModify = () => {
    setShowModifyModal(false);
    setShowFinalDeleteConfirmModal(false);
    setShowMigrateModal(false);
    setShowMigrateReviewModal(false);
    setShowAddUsersModal(false);
    setShowAddUsersReviewModal(false);
    setRoleToModify(null);
    setDeleteConfirmText('');
    setFinalDeleteModalError('');
    setAffectedUsers([]);
    setShowAffectedUsers(false);
    setSelectedMigrateToRole(null);
    setCreateNewMigrateRole(false);
    setNewMigrateRoleName('');
    setSelectedUsersToAdd([]);
    setAvailableUsers([]);
    setUserSearchFilter('');
    setShowAddUsersAffectedUsers(false);
    setShowFinalDeleteAffectedUsers(false);
    setFinalDeleteAffectedUsers([]);
  };

  const handleConfirmDelete = async () => {
    if (!roleToModify) return;
    
    // If there are users with this role, show final confirmation modal
    if (roleToModifyUserCount > 0) {
      setShowModifyModal(false);
      setDeleteConfirmText('');
      setShowFinalDeleteConfirmModal(true);
      return;
    }
    
    // No users with this role - delete directly
    await performDelete();
  };

  const handleBackToModifyFromFinalDelete = () => {
    setShowFinalDeleteConfirmModal(false);
    setDeleteConfirmText('');
    setFinalDeleteModalError('');
    setShowFinalDeleteAffectedUsers(false);
    setFinalDeleteAffectedUsers([]);
    setShowModifyModal(true);
  };

  const handleFinalDeleteConfirm = async () => {
    if (deleteConfirmText !== 'I CLEARly want to delete this role') {
      setFinalDeleteModalError('Please type the confirmation text exactly as shown');
      return;
    }
    
    await performDelete();
    setShowFinalDeleteConfirmModal(false);
    setDeleteConfirmText('');
    setFinalDeleteModalError('');
  };

  const performDelete = async () => {
    if (!roleToModify) return;
    
    try {
      setLoading(true);
      await deleteOfficerRole(roleToModify);
      const deletedRoleName = roleToModify.name;
      
      // Refetch roles to get updated sort orders
      const updatedRoles = await getAllOfficerRoles();
      setRoles(updatedRoles);
      
      setShowModifyModal(false);
      setRoleToModify(null);
      const userText = roleToModifyUserCount === 1 ? '1 user' : `${roleToModifyUserCount} users`;
      const message = roleToModifyUserCount > 0
        ? `Role "${deletedRoleName}" deleted successfully! (${userText} affected)`
        : `Role "${deletedRoleName}" deleted successfully!`;
      toast.success(message);
    } catch (error) {
      console.error('Error deleting role:', error);
      const errorMsg = (error as any).response?.data?.message || (error as any).message || 'Failed to delete role';
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const handleShowMigrateModal = () => {
    setShowModifyModal(false);
    setShowMigrateModal(true);
    setSelectedMigrateToRole(null);
    setCreateNewMigrateRole(false);
    setNewMigrateRoleName('');
  };

  const handleBackToModifyModal = () => {
    setShowMigrateModal(false);
    setShowModifyModal(true);
  };

  const handleShowMigrateReview = async () => {
    // Validate that the new role name doesn't already exist
    if (createNewMigrateRole && newMigrateRoleName.trim()) {
      const roleExists = roles.some(r => r.name.toLowerCase() === newMigrateRoleName.trim().toLowerCase());
      if (roleExists) {
        toast.error('A role with this name already exists. Please choose a different name.');
        return;
      }
    }
    
    setShowMigrateModal(false);
    setShowMigrateReviewModal(true);
    setShowAffectedUsers(false);
    setAffectedUsers([]);
  };

  const handleBackToMigrateModal = () => {
    setShowMigrateReviewModal(false);
    setShowMigrateModal(true);
  };

  const handleToggleAffectedUsers = async () => {
    if (!showAffectedUsers && affectedUsers.length === 0 && roleToModify) {
      // Fetch users if not already loaded
      try {
        const response = await axios.get(`/api/officerRoles/${roleToModify.id}/users`);
        setAffectedUsers(response.data.users || []);
      } catch (error) {
        console.error('Error fetching affected users:', error);
        toast.error('Failed to load affected users');
      }
    }
    setShowAffectedUsers(!showAffectedUsers);
  };

  const handleToggleFinalDeleteAffectedUsers = async () => {
    if (!showFinalDeleteAffectedUsers && finalDeleteAffectedUsers.length === 0 && roleToModify) {
      // Fetch users if not already loaded
      try {
        const response = await axios.get(`/api/officerRoles/${roleToModify.id}/users`);
        setFinalDeleteAffectedUsers(response.data.users || []);
      } catch (error) {
        console.error('Error fetching affected users:', error);
        toast.error('Failed to load affected users');
      }
    }
    setShowFinalDeleteAffectedUsers(!showFinalDeleteAffectedUsers);
  };

  const handleMigrateRole = async () => {
    if (!roleToModify) return;
    
    let targetRoleId = selectedMigrateToRole;
    let targetRoleName = '';
    
    // If creating a new role for migration
    if (createNewMigrateRole && newMigrateRoleName.trim()) {
      try {
        const maxSortOrder = roles.length > 0 ? Math.max(...roles.map((r) => r.sortOrder)) : 0;
        const newRole = await addOfficerRole({
          name: newMigrateRoleName.trim(),
          sortOrder: maxSortOrder + 1,
        });
        targetRoleId = newRole.id;
        targetRoleName = newRole.name;
        setRoles((prevRoles) => [...prevRoles, newRole].sort((a, b) => a.sortOrder - b.sortOrder));
      } catch (error) {
        console.error('Error creating new role:', error);
        const errorMsg = (error as any).response?.data?.error || (error as any).message || 'Failed to create new role for migration';
        toast.error(errorMsg);
        return;
      }
    } else {
      const targetRole = roles.find((r) => r.id === targetRoleId);
      targetRoleName = targetRole?.name || '';
    }
    
    try {
      setLoading(true);
      await axios.post('/api/officerRoles/migrate', {
        fromRoleId: roleToModify.id,
        toRoleId: targetRoleId,
      });
      
      const deletedRoleName = roleToModify.name;
      const migratedUserCount = roleToModifyUserCount;
      
      // Refetch roles to get updated sort orders after deletion
      const updatedRoles = await getAllOfficerRoles();
      setRoles(updatedRoles);
      
      setShowMigrateModal(false);
      setShowMigrateReviewModal(false);
      setRoleToModify(null);
      const userText = migratedUserCount === 1 ? '1 user' : `${migratedUserCount} users`;
      const message = migratedUserCount > 0
        ? `Role "${deletedRoleName}" migrated to "${targetRoleName}" and deleted successfully! (${userText} migrated)`
        : `Role "${deletedRoleName}" migrated to "${targetRoleName}" and deleted successfully!`;
      toast.success(message);
    } catch (error) {
      console.error('Error migrating role:', error);
      const errorMsg = (error as any).response?.data?.message || (error as any).message || 'Failed to migrate role';
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const handleShowAddUsers = async () => {
    if (!roleToModify) return;
    setRoleToAddUsers(roleToModify);
    setShowModifyModal(false);
    setShowAddUsersModal(true);
    setSelectedUsersToAdd([]);
    setUserSearchFilter('');
    
    // Fetch available users (users without this role)
    try {
      setLoadingAvailableUsers(true);
      const response = await axios.get(`/api/officerRoles/${roleToModify.id}/available-users`);
      setAvailableUsers(response.data.users || []);
    } catch (error) {
      console.error('Error fetching available users:', error);
      toast.error('Failed to load available users');
    } finally {
      setLoadingAvailableUsers(false);
    }
  };

  const handleBackToModifyFromAddUsers = () => {
    setShowAddUsersModal(false);
    setSelectedUsersToAdd([]);
    setAvailableUsers([]);
    setUserSearchFilter('');
    setShowAddUsersReviewModal(false);
    setShowAddUsersAffectedUsers(false);
    setShowModifyModal(true);
  };

  const handleShowAddUsersReview = () => {
    setShowAddUsersModal(false);
    setShowAddUsersReviewModal(true);
    setShowAddUsersAffectedUsers(false);
  };

  const handleBackToAddUsersFromReview = () => {
    setShowAddUsersReviewModal(false);
    setShowAddUsersAffectedUsers(false);
    setShowAddUsersModal(true);
  };

  const handleToggleAddUsersAffectedUsers = () => {
    setShowAddUsersAffectedUsers(!showAddUsersAffectedUsers);
  };

  const handleToggleUserSelection = (userId: number) => {
    setSelectedUsersToAdd(prev => {
      if (prev.includes(userId)) {
        return prev.filter(id => id !== userId);
      } else {
        return [...prev, userId];
      }
    });
  };

  const handleConfirmAddUsers = async () => {
    if (!roleToAddUsers || selectedUsersToAdd.length === 0) return;
    
    try {
      setLoading(true);
      await axios.post('/api/officerRoles/add-users', {
        roleId: roleToAddUsers.id,
        userIds: selectedUsersToAdd
      });
      
      setShowAddUsersModal(false);
      setShowAddUsersReviewModal(false);
      setSelectedUsersToAdd([]);
      setAvailableUsers([]);
      setUserSearchFilter('');
      setRoleToModify(null);
      const userText = selectedUsersToAdd.length === 1 ? '1 user' : `${selectedUsersToAdd.length} users`;
      toast.success(`Successfully added ${userText} to role "${roleToAddUsers.name}"`);
    } catch (error) {
      console.error('Error adding users:', error);
      const errorMsg = (error as any).response?.data?.message || (error as any).message || 'Failed to add users';
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const moveRoleUp = async (roleId: number) => {
    // Use sortOrder-based sorting (not alphabetical)
    const currentRoles = [...roles].sort((a, b) => a.sortOrder - b.sortOrder);
    const roleIndex = currentRoles.findIndex((role) => role.id === roleId);

    if (roleIndex > 0) {
      const newRoles = [...currentRoles];

      // Swap the sortOrder values
      const temp = newRoles[roleIndex].sortOrder;
      newRoles[roleIndex].sortOrder = newRoles[roleIndex - 1].sortOrder;
      newRoles[roleIndex - 1].sortOrder = temp;

      // Update local state
      setRoles(newRoles);

      // Update backend with the correct format
      try {
        await updateSortOrder({
          roles: [
            {
              id: newRoles[roleIndex - 1].id,
              sort_order: newRoles[roleIndex - 1].sortOrder,
            },
            {
              id: newRoles[roleIndex].id,
              sort_order: newRoles[roleIndex].sortOrder,
            },
          ],
        });
        const roleName = newRoles[roleIndex].name;
        toast.success(`Successfully moved "${roleName}" up`);
      } catch (error) {
        console.error('Error updating sort order:', error);
        toast.error('Failed to reorder roles. Please try again.');
        // Revert on error
        setRoles(currentRoles);
      }
    }
  };

  const moveRoleDown = async (roleId: number) => {
    // Use sortOrder-based sorting (not alphabetical)
    const currentRoles = [...roles].sort((a, b) => a.sortOrder - b.sortOrder);
    const roleIndex = currentRoles.findIndex((role) => role.id === roleId);

    if (roleIndex >= 0 && roleIndex < currentRoles.length - 1) {
      const newRoles = [...currentRoles];

      // Swap the sortOrder values
      const temp = newRoles[roleIndex].sortOrder;
      newRoles[roleIndex].sortOrder = newRoles[roleIndex + 1].sortOrder;
      newRoles[roleIndex + 1].sortOrder = temp;

      // Update local state
      setRoles(newRoles);

      // Send to backend
      try {
        const payload = {
          roles: [
            {
              id: newRoles[roleIndex].id,
              sort_order: newRoles[roleIndex].sortOrder,
            },
            {
              id: newRoles[roleIndex + 1].id,
              sort_order: newRoles[roleIndex + 1].sortOrder,
            },
          ],
        };

        await updateSortOrder(payload);
        const roleName = newRoles[roleIndex].name;
        toast.success(`Successfully moved "${roleName}" down`);
      } catch (error) {
        console.error('Error updating sort order:', error);
        toast.error('Failed to reorder roles. Please try again.');
        // Revert on error
        setRoles(currentRoles);
      }
    }
  };

  const columns: Array<DataGridColumn<OfficerRole>> = [
    {
      id: 'sortOrder',
      header: 'Order',
      accessor: (row) => row.sortOrder,
      sortable: true,
      width: '80px',
      align: 'center',
      editable: {
        type: 'number',
        validate: (newValue) => {
          const numValue = parseInt(newValue, 10);
          if (isNaN(numValue) || numValue < 1) {
            return {
              valid: false,
              message: 'Order must be a positive number',
            };
          }
          return true;
        },
        onSave: saveSortOrder,
      },
    },
    {
      id: 'name',
      header: 'Role Name',
      accessor: (row) => row.name,
      sortable: true,
      editable: {
        type: 'text',
        validate: (newValue, row) => {
          if (!newValue || newValue.trim().length === 0) {
            return {
              valid: false,
              message: 'Role name is required',
            };
          }
          // Exclude the current row from duplicate check
          if (
            roles.some(
              (role) => role.id !== row.id && role.name === newValue.trim()
            )
          ) {
            return {
              valid: false,
              message: 'Role name already exists',
            };
          }
          return true;
        },
        onSave: (row, value) => saveRoleName(row, value.trim()),
      },
    },
    {
      id: 'moveActions',
      header: 'Reorder',
      width: '120px',
      align: 'center',
      cell: (row) => {
        // ALWAYS use sortOrder-based sorting for button logic (ignore display mode)
        const currentSortedRoles = [...roles].sort(
          (a, b) => a.sortOrder - b.sortOrder
        );
        const roleIndex = currentSortedRoles.findIndex(
          (role) => role.id === row.id
        );

        return (
          <div style={{ display: 'flex', gap: '4px', justifyContent: 'center', alignItems: 'center', width: '100%' }}>
            <PrismButton
              tone="primary" display="outline"
              size="sm"
              onPrismClick={() => moveRoleUp(row.id)}
              disabled={roleIndex === 0}
              title="Move Up"
            >
              ↑
            </PrismButton>
            <PrismButton
              tone="primary" display="outline"
              size="sm"
              onPrismClick={() => moveRoleDown(row.id)}
              disabled={roleIndex === currentSortedRoles.length - 1}
              title="Move Down"
            >
              ↓
            </PrismButton>
          </div>
        );
      },
    },
    {
      id: 'actions',
      header: 'Actions',
      width: '100px',
      align: 'center',
      cell: (row) => (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%' }}>
          <PrismButton
            tone="caution"
            size="sm"
            onPrismClick={() => handleModifyClick(row.id)}
            title="Modify Role"
          >
            Modify
          </PrismButton>
        </div>
      ),
    },
  ];

  return (
    <AdminPane as="div" width="wide" className="admin officer-roles-container">
      <div className="officer-roles-header">
        <PrismBanner tone="info" variant="system" dismissible={false} className="clr:mb-4">
          <strong>Multiple Roles:</strong>
        <ul className="clr:mb-0 clr:mt-1" style={{ paddingLeft: '1.5rem', listStyleType: 'disc', lineHeight: '1.4' }}>
          <li>Officers can hold multiple roles simultaneously (e.g., an officer can be both "Triad Committee Member" and "DE&I Lead")</li>
        </ul>
        <strong className="clr:mt-2 clr:block">Sorting:</strong>
        <ul className="clr:mb-0 clr:mt-1" style={{ paddingLeft: '1.5rem', listStyleType: 'disc', lineHeight: '1.4' }}>
          <li>Toggle between Alphabetical and Random sorting to control how officer bios are displayed on the About page
            <ul className="clr:mt-1" style={{ paddingLeft: '1.5rem', listStyleType: 'circle', lineHeight: '1.4' }}>
              <li>Alphabetical mode sorts roles A-Z when Order numbers are equal</li>
              <li>Random mode shuffles roles when Order numbers are equal</li>
            </ul>
          </li>
        </ul>
        <strong className="clr:mt-2 clr:block">Role Ordering:</strong>
        <ul className="clr:mb-0 clr:mt-1" style={{ paddingLeft: '1.5rem', listStyleType: 'disc', lineHeight: '1.4' }}>
          <li>Roles are sorted by their Order number</li>
          <li>Lower numbers indicate higher priority/rank (e.g., 1 = highest rank, 2 = second highest, etc.)</li>
          <li>You can reorder roles using the up/down arrows or by editing the Order number directly</li>
        </ul>
        </PrismBanner>

        <div className="officer-roles-form-grid">
          <div>
          <div className="officer-roles-field clr:mb-2">
            <label className="officer-roles-label" htmlFor="newRoleInput">Add New Role</label>
            <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
              <PrismInputText
                ref={newRoleInputRef}
                label="Add New Role"
                hideLabel
                id="newRoleInput"
                type="text"
                value={newRole}
                onPrismInput={(e: CustomEvent<{ value?: string }>) => setNewRole(e.detail?.value ?? '')}
                onPrismKeydown={(e: CustomEvent<{ key?: string }>) => {
                  if (e.detail?.key === 'Enter') {
                    e.preventDefault();
                    handleAdd();
                  }
                }}
                placeholder="Enter role name"
                style={{ flex: 1 }}
              />
              <PrismButton
                onPrismClick={handleAdd}
                tone="primary"
                style={{ whiteSpace: 'nowrap' }}
                disabled={!newRole.trim() || roles.some((role) => role.name === newRole.trim())}
              >
                Add Role
              </PrismButton>
            </div>
          </div>
          <div className="officer-roles-field clr:mb-0">
            <label className="officer-roles-label">Sort by Alphabetical OR Random Order</label>
            <PrismSwitch
              name="sort-order-switch"
              id="sort-order-switch"
              label={`Sort: ${sortOrder === 'alphabetical' ? 'Alphabetical' : 'Random'}`}
              checked={sortOrder === 'random'}
              onPrismChange={toggleSortOrder}
            />
          </div>
        </div>
      </div>
      </div>

      {loading ? (
        <div className="clr:text-center">
          <PrismLoading as="spinner" container="inline" />
        </div>
      ) : (
        <div className="officer-roles-table-wrapper">
          <PrismDataGrid
            keyField="id"
            caption="Officer roles"
            data={sortedRoles}
            columns={columns}
            className="admin-tbl"
            onValidationError={(message) => toast.error(message)}
          />
        </div>
      )}

      {/* Modify Role Modal */}
      <PrismDialog open={showModifyModal} close-method="user" setAriaLabel="Modify Role" onPrismClose={handleCancelModify}>
        <span slot="header"><i className="fas fa-edit"></i>
            Modify Role</span>
        <div>
          <p>What would you like to do with the role:</p>
          <p className="clr:text-center">"{roleToModify?.name}"</p>

          <PrismBanner
            tone="caution"
            variant="system"
            dismissible={false}
            style={{
              color: 'var(--color-text)'
            }}
          >
            <div className="clr:flex clr:items-center">
              <i className="fas fa-info-circle"></i>
              <small>
                This role is currently assigned to{' '}
                <strong>{roleToModifyUserCount}</strong> user(s).
                You can add more users to this role, migrate users to another role, or delete the role.
              </small>
            </div>
          </PrismBanner>
        </div>

          <PrismButton slot="footer" tone="secondary" display="outline" onPrismClick={handleCancelModify}>
            Cancel
          </PrismButton>
          <PrismButton slot="footer"
            tone="success"
            onPrismClick={handleShowAddUsers}
          >
            <i className="fas fa-plus"></i>
            Add Users
          </PrismButton>
          {roleToModifyUserCount > 0 && (
            <PrismButton slot="footer"
              tone="info" display="outline"
              onPrismClick={handleShowMigrateModal}
            >
              Migrate
            </PrismButton>
          )}
          <PrismButton slot="footer" tone="danger" onPrismClick={handleConfirmDelete}>
            <i className="fas fa-trash"></i>
            Delete Role
          </PrismButton>

      </PrismDialog>

      {/* Migrate Modal */}
      <PrismDialog open={showMigrateModal} close-method="user" setAriaLabel={`Migrate Role: ${roleToModify?.name ?? ''}`} onPrismClose={handleCancelModify}>
        <span slot="header">Migrate Role: "{roleToModify?.name}"</span>
        <div>
          <p>
            All {roleToModifyUserCount} user(s) currently assigned "{roleToModify?.name}"
            will be updated to use the selected role instead.
          </p>

          <PrismBanner tone="info" variant="system" dismissible={false}>
            <div className="clr:flex clr:items-start">
              <i className="fas fa-info-circle clr:mt-1"></i>
              <div>
                <strong>What happens when you migrate:</strong>
                <ul className="clr:mb-0 clr:mt-2" style={{ paddingLeft: '1.5rem', listStyleType: 'disc' }}>
                  <li>All users will be assigned to the selected role</li>
                  <li>The role "{roleToModify?.name}" will be deleted</li>
                  <li>This action cannot be undone</li>
                </ul>
              </div>
            </div>
          </PrismBanner>

          <div className="officer-roles-field clr:mb-4">
            <label className="officer-roles-label" htmlFor="migrateRoleSelect">Select destination role:</label>
            <PrismSelect
              id="migrateRoleSelect"
              label="Select destination role"
              hideLabel
              value={selectedMigrateToRole?.toString() || ''}
              options={[
                { value: '', display: 'Select a role...' },
                ...roles.filter(r => r.id !== roleToModify?.id).map(role => ({
                  value: role.id.toString(),
                  display: role.name,
                })),
              ]}
              onPrismChange={(e: CustomEvent<{ value?: string }>) => {
                const value = e.detail?.value || '';
                setSelectedMigrateToRole(value ? parseInt(value, 10) : null);
                if (value) {
                  setCreateNewMigrateRole(false);
                }
              }}
              disabled={createNewMigrateRole}
            />
          </div>

          <div className="officer-roles-field clr:mb-4">
            <PrismCheckbox
              id="createNewMigrateRoleCheckbox"
              label="Create a new role to migrate to"
              checked={createNewMigrateRole}
              onPrismChange={(e: CustomEvent<{ checked?: boolean }>) => {
                const checked = !!e.detail?.checked;
                setCreateNewMigrateRole(checked);
                if (checked) {
                  setSelectedMigrateToRole(null);
                }
              }}
            />
          </div>

          {createNewMigrateRole && (
            <div className="officer-roles-field clr:mb-4">
              <label className="officer-roles-label" htmlFor="newMigrateRoleName">New role name:</label>
              <PrismInputText
                label="New role name"
                hideLabel
                type="text"
                id="newMigrateRoleName"
                name="newMigrateRoleName"
                value={newMigrateRoleName}
                onPrismInput={(e: CustomEvent<{ value?: string }>) => setNewMigrateRoleName(e.detail?.value ?? '')}
                placeholder="Enter new role name"
                invalid={newMigrateRoleName.trim() !== '' && roles.some(r => r.name.toLowerCase() === newMigrateRoleName.trim().toLowerCase())}
                invalid-message="A role with this name already exists"
              />
            </div>
          )}
        </div>

          <PrismButton slot="footer" tone="secondary" display="outline" onPrismClick={handleCancelModify}>
            Cancel
          </PrismButton>
          <PrismButton slot="footer" tone="info" display="outline" onPrismClick={handleBackToModifyModal}>
            Back
          </PrismButton>
          <PrismButton slot="footer"
            tone="success"
            onPrismClick={handleShowMigrateReview}
            disabled={
              !selectedMigrateToRole &&
              (!createNewMigrateRole ||
                !newMigrateRoleName.trim() ||
                roles.some(r => r.name.toLowerCase() === newMigrateRoleName.trim().toLowerCase())
              )
            }
          >
            <i className="fas fa-check-circle"></i>
            Review
          </PrismButton>

      </PrismDialog>

      {/* Add Users Modal */}
      <PrismDialog open={showAddUsersModal} close-method="user" setAriaLabel={`Add Users to Role: ${roleToAddUsers?.name ?? ''}`} onPrismClose={handleCancelModify}>
        <span slot="header"><i className="fas fa-plus-circle"></i>
            Add Users to Role: "{roleToAddUsers?.name}"</span>
        <div style={{ height: '500px', display: 'flex', flexDirection: 'column' }}>
          {loadingAvailableUsers ? (
            <div className="clr:flex clr:justify-center clr:items-center" style={{ flex: 1 }}>
              <PrismLoading as="spinner" container="inline" />
            </div>
          ) : (
            <>
              <div style={{ marginBottom: '1rem' }}>
                <p className="clr:mb-2">
                  Select users to add to "{roleToAddUsers?.name}":
                </p>
                <div style={{ position: 'relative' }}>
                  <PrismInputText
                    type="text"
                    label="Search users by name or email"
                    placeholder="Search users by name or email..."
                    value={userSearchFilter}
                    onPrismInput={(e: CustomEvent<{ value?: string }>) => setUserSearchFilter(e.detail?.value ?? '')}
                    className="clr:mb-4"
                    style={{ paddingRight: userSearchFilter ? '2.5rem' : undefined }}
                  />
                  {userSearchFilter && (
                    <PrismButton
                      tone="primary" display="link"
                      size="sm"
                      onPrismClick={() => setUserSearchFilter('')}
                      style={{
                        position: 'absolute',
                        right: '0.5rem',
                        top: '0.25rem',
                        padding: '0.25rem 0.5rem',
                        color: 'var(--color-text-faded)',
                        textDecoration: 'none'
                      }}
                      title="Clear search"
                    >
                      <i className="fas fa-times"></i>
                    </PrismButton>
                  )}
                </div>
              </div>

              <div style={{ flex: 1, overflowY: 'auto', border: '1px solid var(--color-border)', borderRadius: '0.25rem', padding: '1rem' }}>
                {availableUsers.length === 0 && !userSearchFilter && (
                  <div className="clr:text-secondary-base clr:text-center clr:py-4">
                    No available users found. All users may already have an officer role assigned.
                  </div>
                )}
                <div>
                  {availableUsers.filter(user =>
                    user.name.toLowerCase().includes(userSearchFilter.toLowerCase()) ||
                    user.email.toLowerCase().includes(userSearchFilter.toLowerCase())
                  ).map(user => (
                    <PrismCheckbox
                      key={user.id}
                              id={`user-${user.id}`}
                      label={`${user.name} (${user.email})`}
                      checked={selectedUsersToAdd.includes(user.id)}
                      onPrismChange={(e: CustomEvent<{ checked?: boolean }>) => { if (e.detail?.checked !== undefined) handleToggleUserSelection(user.id); }}
                      className="clr:mb-2"
                    />
                  ))}
                  {availableUsers.filter(user =>
                    user.name.toLowerCase().includes(userSearchFilter.toLowerCase()) ||
                    user.email.toLowerCase().includes(userSearchFilter.toLowerCase())
                  ).length === 0 && userSearchFilter && (
                    <div className="clr:text-secondary-base clr:text-center clr:py-4">
                      No users match your search.
                    </div>
                  )}
                </div>
              </div>

              <div style={{ minHeight: '60px', flexShrink: 0 }}>
                {selectedUsersToAdd.length > 0 && (
                  <PrismBanner tone="success" variant="system" dismissible={false} className="clr:mt-4 clr:mb-0">
                    <i className="fas fa-check-circle"></i>
                    {selectedUsersToAdd.length} user(s) will be assigned role "{roleToAddUsers?.name}"
                  </PrismBanner>
                )}
              </div>
            </>
          )}
        </div>

          <PrismButton slot="footer" tone="secondary" display="outline" onPrismClick={handleCancelModify}>
            Cancel
          </PrismButton>
          <PrismButton slot="footer" tone="info" display="outline" onPrismClick={handleBackToModifyFromAddUsers}>
            Back
          </PrismButton>
          <PrismButton slot="footer"
            tone="success"
            onPrismClick={handleShowAddUsersReview}
            disabled={selectedUsersToAdd.length === 0}
          >
            <i className="fas fa-check-circle"></i>
            Review
          </PrismButton>

      </PrismDialog>

      {/* Add Users Review Modal */}
      <PrismDialog open={showAddUsersReviewModal} close-method="user" setAriaLabel="Review Add Users" onPrismClose={handleCancelModify}>
        <span slot="header"><i className="fas fa-check-circle"></i>
            Review Add Users</span>
        <div>
          <p className="clr:mb-4">Please review the details before adding:</p>

          <div className="officer-roles-boxed clr:p-4 clr:mb-4" style={{ backgroundColor: 'var(--color-surface-alt)' }}>
            <ul className="clr:list-none clr:pl-0 clr:mb-0">
              <li>
                <strong>Users to add:</strong>
                <ul className="clr:list-none clr:pl-0 clr:mt-1">
                  <li>
                    <i className="fas fa-users"></i>{selectedUsersToAdd.length} user(s)
                    <PrismButton
                      tone="primary" display="link"
                      size="sm"
                      onPrismClick={handleToggleAddUsersAffectedUsers}
                      setAriaExpanded={showAddUsersAffectedUsers}
                      className="clr:p-0"
                      style={{ textDecoration: 'none', fontSize: '0.875rem' }}
                    >
                      <i className={`fas fa-chevron-${showAddUsersAffectedUsers ? 'up' : 'down'}`}></i>
                      {showAddUsersAffectedUsers ? 'Hide' : 'Show'} users
                    </PrismButton>
                  </li>
                </ul>
                {showAddUsersAffectedUsers && (
                  <div id="add-users-affected-users-collapse" className="clr:mt-2">
                      <small className="clr:text-secondary-base">Selected users:</small>
                      <ul className="clr:mb-0 clr:mt-1" style={{ paddingLeft: '1.5rem', listStyleType: 'disc' }}>
                        {availableUsers.filter(u => selectedUsersToAdd.includes(u.id)).map(user => (
                          <li key={user.id}><small>{user.name} ({user.email})</small></li>
                        ))}
                      </ul>
                    </div>

                )}
              </li>
            </ul>
          </div>

          <PrismBanner tone="info" variant="system" dismissible={false}>
            <div className="clr:flex clr:items-start">
              <i className="fas fa-info-circle clr:mt-1"></i>
              <div>
                <strong>This action will:</strong>
                <ul className="clr:mb-0 clr:mt-2" style={{ paddingLeft: '1.5rem', listStyleType: 'disc' }}>
                  <li>Add the role "{roleToAddUsers?.name}" to {selectedUsersToAdd.length} user(s)</li>
                </ul>
              </div>
            </div>
          </PrismBanner>
        </div>

          <PrismButton slot="footer" tone="secondary" display="outline" onPrismClick={handleCancelModify}>
            Cancel
          </PrismButton>
          <PrismButton slot="footer" tone="info" display="outline" onPrismClick={handleBackToAddUsersFromReview}>
            Back
          </PrismButton>
          <PrismButton slot="footer" tone="success" onPrismClick={handleConfirmAddUsers}>
            <i className="fas fa-check"></i>
            Confirm Add {selectedUsersToAdd.length} User{selectedUsersToAdd.length !== 1 ? 's' : ''}
          </PrismButton>

      </PrismDialog>

      {/* Migrate Review Modal */}
      <PrismDialog open={showMigrateReviewModal} close-method="user" setAriaLabel="Review Migration" onPrismClose={handleCancelModify}>
        <span slot="header"><i className="fas fa-check-circle"></i>
            Review Migration</span>
        <div>
          <p className="clr:mb-4">Please review the migration details before confirming:</p>

          <div className="officer-roles-boxed clr:p-4 clr:mb-4" style={{ backgroundColor: 'var(--color-surface-alt)' }}>
            <ul className="clr:list-none clr:pl-0 clr:mb-0">
              <li className="clr:mb-2">
                <strong>From:</strong> <small className="clr:text-secondary-base">(will be deleted)</small>
                <ul className="clr:list-none clr:pl-0 clr:mt-1">
                  <li className="clr:text-danger-base">
                    <i className="fas fa-minus-circle"></i>
                    <i className="fas fa-user-tag"></i>"{roleToModify?.name}"
                  </li>
                </ul>
              </li>
              <li className="clr:mb-2">
                <strong>To:</strong> <small className="clr:text-secondary-base">(destination)</small>
                <ul className="clr:list-none clr:pl-0 clr:mt-1">
                  <li className="clr:text-success-base">
                    <i className="fas fa-arrow-circle-right"></i>
                    <i className="fas fa-user-tag"></i>
                    {createNewMigrateRole ? (
                      <span>"{newMigrateRoleName}"</span>
                    ) : (
                      <span>"{roles.find(r => r.id === selectedMigrateToRole)?.name}"</span>
                    )}
                  </li>
                </ul>
              </li>
              <li>
                <strong>Users affected:</strong>
                <ul className="clr:list-none clr:pl-0 clr:mt-1">
                  <li>
                    <i className="fas fa-users"></i>{roleToModifyUserCount} user(s)
                    {roleToModifyUserCount > 0 && (
                      <PrismButton
                        tone="primary" display="link"
                        size="sm"
                        onPrismClick={handleToggleAffectedUsers}
                        setAriaExpanded={showAffectedUsers}
                        className="clr:p-0"
                        style={{ textDecoration: 'none', fontSize: '0.875rem' }}
                      >
                        <i className={`fas fa-chevron-${showAffectedUsers ? 'up' : 'down'}`}></i>
                        {showAffectedUsers ? 'Hide' : 'Show'} users
                      </PrismButton>
                    )}
                  </li>
                </ul>
                {roleToModifyUserCount > 0 && (
                  <div id="affected-users-collapse" className="clr:mt-2">
                      {affectedUsers.length > 0 ? (
                        <ul className="clr:mb-0" style={{ paddingLeft: '1.5rem', listStyleType: 'disc' }}>
                          {affectedUsers.map(user => (
                            <li key={user.id}><small>{user.name} ({user.email})</small></li>
                          ))}
                        </ul>
                      ) : (
                        <PrismLoading as="spinner" size="sm" container="inline" />
                      )}
                    </div>

                )}
              </li>
            </ul>
          </div>

          <PrismBanner tone="info" variant="system" dismissible={false}>
            <div className="clr:flex clr:items-start">
              <i className="fas fa-info-circle clr:mt-1"></i>
              <div>
                <strong>This action will:</strong>
                <ul className="clr:mb-0 clr:mt-2" style={{ paddingLeft: '1.5rem', listStyleType: 'disc' }}>
                  <li>Update all {roleToModifyUserCount} user(s) to use the new role</li>
                  {createNewMigrateRole && <li>Create the role "{newMigrateRoleName}"</li>}
                  <li>Permanently delete the role "{roleToModify?.name}"</li>
                </ul>
              </div>
            </div>
          </PrismBanner>
        </div>

          <PrismButton slot="footer" tone="secondary" display="outline" onPrismClick={handleCancelModify}>
            Cancel
          </PrismButton>
          <PrismButton slot="footer" tone="info" display="outline" onPrismClick={handleBackToMigrateModal}>
            Back
          </PrismButton>
          <PrismButton slot="footer" tone="danger" onPrismClick={handleMigrateRole}>
            <i className="fas fa-check"></i>
            Confirm Migration & Delete
          </PrismButton>

      </PrismDialog>

      {/* Final Delete Confirmation Modal */}
      <PrismDialog open={showFinalDeleteConfirmModal} close-method="user" setAriaLabel="Final Confirmation Required" onPrismClose={handleCancelModify}>
        <span slot="header"><i className="fas fa-exclamation-triangle clr:text-danger-base"></i>
            Final Confirmation Required</span>
        <div>
          <p>You are about to permanently delete the role:</p>
          <p className="clr:text-center">"{roleToModify?.name}"</p>

          <PrismBanner tone="danger" variant="system" dismissible={false}>
            <div className="clr:flex clr:items-start">
              <i className="fas fa-exclamation-triangle clr:mt-1"></i>
              <div>
                <small>
                  This will affect <strong>{roleToModifyUserCount}</strong> user(s).
                  This action cannot be undone.
                </small>
                {roleToModifyUserCount > 0 && (
                  <div className="clr:mt-2">
                    <PrismButton
                      tone="primary" display="link"
                      size="sm"
                      onPrismClick={handleToggleFinalDeleteAffectedUsers}
                      setAriaExpanded={showFinalDeleteAffectedUsers}
                      className="clr:p-0"
                      style={{ textDecoration: 'none', fontSize: '0.875rem', color: 'var(--color-danger, var(--color-text))' }}
                    >
                      <i className={`fas fa-chevron-${showFinalDeleteAffectedUsers ? 'up' : 'down'}`}></i>
                      {showFinalDeleteAffectedUsers ? 'Hide' : 'Show'} affected users
                    </PrismButton>
                  </div>
                )}
              </div>
            </div>
          </PrismBanner>

          {roleToModifyUserCount > 0 && (
            <div id="final-delete-affected-users-collapse" className="clr:mb-4">
                <div className="officer-roles-boxed clr:p-2" style={{ backgroundColor: 'var(--color-surface-alt)', maxHeight: '200px', overflowY: 'auto' }}>
                  <small className="clr:text-secondary-base">Affected users:</small>
                  {finalDeleteAffectedUsers.length === 0 ? (
                    <div className="clr:text-center clr:py-2">
                      <PrismLoading as="spinner" size="sm" container="inline" />
                    </div>
                  ) : (
                    <ul className="clr:mb-0" style={{ paddingLeft: '1.5rem', listStyleType: 'disc' }}>
                      {finalDeleteAffectedUsers.map(user => (
                        <li key={user.id}><small>{user.name} ({user.email})</small></li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>

          )}

          <div className="officer-roles-field clr:mt-4">
            <label className="officer-roles-label" htmlFor="deleteConfirmInput">
              Type <strong>"I CLEARly want to delete this role"</strong> to confirm:
            </label>
            <PrismInputText
              type="text"
              label="Type the confirmation text to delete this role"
              hideLabel
              id="deleteConfirmInput"
              name="deleteConfirmText"
              value={deleteConfirmText}
              onPrismInput={(e: CustomEvent<{ value?: string }>) => {
                setDeleteConfirmText(e.detail?.value ?? '');
                setFinalDeleteModalError('');
              }}
              placeholder="I CLEARly want to delete this role"
              set-autofocus
            />
            {finalDeleteModalError && (
              <PrismBanner tone="danger" variant="system" dismissible={false} className="clr:mt-2 clr:mb-0">
                <i className="fas fa-exclamation-circle"></i>
                {finalDeleteModalError}
              </PrismBanner>
            )}
          </div>
        </div>

          <PrismButton slot="footer" tone="secondary" display="outline" onPrismClick={handleCancelModify}>
            Cancel
          </PrismButton>
          <PrismButton slot="footer" tone="info" display="outline" onPrismClick={handleBackToModifyFromFinalDelete}>
            Back
          </PrismButton>
          <PrismButton slot="footer" tone="danger" onPrismClick={handleFinalDeleteConfirm}>
            <i className="fas fa-trash"></i>
            Confirm Delete
          </PrismButton>

      </PrismDialog>
    </AdminPane>
  );
};

export default OfficerRoles;
